import { Grid, GridItem, Input, Textarea } from '@chakra-ui/react'
import React from 'react'

import { CharCount } from '../../../../components/CharCount'
import { FormField } from '../../../../components/FormField'
import { RichTextField } from '../../../../components/RichTextField'
import { FormSelect } from '../../../../components/Select'
import { ICoursePageFields, TCourseAgeType } from '../../../../services/CoursesService/interfaces'

export const HERO_DESCRIPTION_MAX_LENGTH = 220

interface IProps<T extends ICoursePageFields> {
  formData: T
  setFormData: React.Dispatch<React.SetStateAction<T>>
  errors: Record<string, string[]>
  /** O que o site mostra com a descrição vazia: o texto padrão é de cada modalidade. */
  heroDescriptionHelp: string
  /** Trava os campos enquanto o formulário salva. */
  disabled?: boolean
}

/**
 * Textos da página do curso no site, presencial (ABRA-406) ou gravado (ABRA-437). Tudo é
 * opcional: sem descrição, o topo usa um texto padrão; sem vídeo, ele fica em uma coluna; sem
 * pré-requisitos nem idade, o card ao lado do "Sobre o curso" não aparece.
 */
export function CoursePageTab<T extends ICoursePageFields>({
  formData,
  setFormData,
  errors,
  heroDescriptionHelp,
  disabled
}: IProps<T>) {
  return (
    <Grid templateColumns="repeat(2, 1fr)" gap={6}>
      <GridItem colSpan={2}>
        <FormField
          label="Descrição do topo"
          htmlFor="hero_description"
          error={errors.hero_description}
          helperText={heroDescriptionHelp}
          isDisabled={disabled}
        >
          <Textarea
            id="hero_description"
            value={formData.hero_description}
            onChange={(e) => setFormData((prev) => ({ ...prev, hero_description: e.target.value }))}
            maxLength={HERO_DESCRIPTION_MAX_LENGTH}
            rows={3}
          />
          <CharCount value={formData.hero_description} max={HERO_DESCRIPTION_MAX_LENGTH} />
        </FormField>
      </GridItem>

      <GridItem colSpan={2}>
        <FormField
          label="Vídeo do topo"
          htmlFor="hero_video_url"
          error={errors.hero_video_url}
          helperText="Link do YouTube. Sem vídeo, o topo fica em uma coluna."
          isDisabled={disabled}
        >
          <Input
            id="hero_video_url"
            type="url"
            value={formData.hero_video_url}
            onChange={(e) => setFormData((prev) => ({ ...prev, hero_video_url: e.target.value }))}
            placeholder="https://www.youtube.com/watch?v=..."
            maxLength={255}
          />
        </FormField>
      </GridItem>

      <GridItem colSpan={2}>
        <RichTextField
          label="Sobre o curso"
          error={errors.about}
          value={formData.about}
          onChange={(about) => setFormData((prev) => ({ ...prev, about }))}
          minH="250px"
          isReadOnly={disabled}
        />
      </GridItem>

      <GridItem colSpan={2}>
        <RichTextField
          label="Pré-requisitos"
          error={errors.prerequisites}
          value={formData.prerequisites}
          onChange={(prerequisites) => setFormData((prev) => ({ ...prev, prerequisites }))}
          minH="80px"
          isReadOnly={disabled}
        />
      </GridItem>

      <GridItem>
        <FormField label="Idade" htmlFor="age_type" error={errors.age_type}>
          <FormSelect
            id="age_type"
            value={formData.age_type}
            isDisabled={disabled}
            // "Sem idade" leva o valor junto: a API recusa valor sem o tipo.
            onChange={(value) =>
              setFormData((prev) => ({
                ...prev,
                age_type: value as TCourseAgeType | '',
                age_value: value ? prev.age_value : ''
              }))
            }
            options={[
              { value: 'minimum', label: 'Idade mínima' },
              { value: 'range', label: 'Faixa etária' }
            ]}
            placeholder="Sem idade"
          />
        </FormField>
      </GridItem>

      <GridItem>
        <FormField label="Valor da idade" htmlFor="age_value" error={errors.age_value}>
          <Input
            id="age_value"
            value={formData.age_value}
            onChange={(e) => setFormData((prev) => ({ ...prev, age_value: e.target.value }))}
            placeholder={formData.age_type === 'range' ? 'Ex.: De 7 a 12 anos' : 'Ex.: 14 anos'}
            maxLength={100}
            isDisabled={disabled || !formData.age_type}
          />
        </FormField>
      </GridItem>
    </Grid>
  )
}
